import { Button, Col, Form, Input, message, Row, Space } from 'antd'
import React, { useContext, useEffect, useState } from 'react'
import { translate } from '../../../core'
import { Uploader } from '../../../core/component/Upload'
import { DeleteOutlined, PlusOutlined, SaveOutlined, UnorderedListOutlined } from '@ant-design/icons'
import { PageContextValue } from '../../../core/type'
import { Appraisal, AppraisalEntry, AppraisalLineDto, AppraisalLineEntry } from '../type'
import { AppraisalPageContext } from './AppraisalPageContext'
import { NumericInput, ShowIf, SinglePageable, SinglePageableResponse } from '../../../core/component'
import { AppraisalFormLinesItem } from './AppraisalFormLinesItem'
import { AppraisalLinesSlider } from './AppraisalLinesSlider'
import { useMutation } from '@apollo/client'
import { AppraisalUpdateLines } from '../graphql/appraisal.graphql'
import { AppraisalReorderModal } from './AppraisalReorderModal'

type AppraisalFormLinesProps = { onChanged: () => void }

export const AppraisalFormLines = ({onChanged}: AppraisalFormLinesProps) => {
  const [linesLength, setLinesLength] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const {entity, form} =
    useContext<PageContextValue<Appraisal, AppraisalEntry>>(AppraisalPageContext)
  const [currentRowIndex, setCurrentRowIndex] = useState<number>()
  const [messageApi, contextHolder] = message.useMessage()
  const [reorderModalOpen, setReorderModalOpen] = useState<boolean>(false)

  const resetLinesLength = (targetPage: 'first' | 'last') => {
    const lines: any[] = form?.getFieldValue('lines')
    const length = lines?.length || 0
    setLinesLength(length)

    let thisPage = 0

    if (length > 0) {
      thisPage = targetPage === 'first'
        ? 1
        : length
    }
    setPage(0)
    setTimeout(() => setPage(thisPage), 500)
    onChanged?.()
  }

  useEffect(() => {
    setTimeout(() => resetLinesLength('first'), 500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity?.id])

  const onChangeSlideChange = async (_: unknown, page: number): Promise<SinglePageableResponse<any>> => {
    if (!linesLength)
      return {count: linesLength}

    setCurrentRowIndex(page - 1)

    return {
      count: linesLength
    }
  }

  const [update, {loading: updating}] = useMutation(AppraisalUpdateLines, {
    onCompleted: () => messageApi.success(translate('appraisal', 'lines', 'save_success_message')),
    onError: (err) => messageApi.error(err.message),
  })

  return (
    <Form.List name='lines'>
      {(fields, {add, remove}) => (
        <>
          <div className='mt-2 d-flex flex-row'>
            <div className='w-75 d-flex flex-row'>
              <Button
                onClick={() => {
                  add({})
                  resetLinesLength('last')
                }}
                icon={<PlusOutlined/>}
                className='btn-primary-gradient'
              >
                {translate('add', 'item')}
              </Button>

              <Button
                disabled={!linesLength}
                className='ms-1'
                danger
                onClick={() => {
                  typeof currentRowIndex === 'number' && remove(currentRowIndex)
                  resetLinesLength('first')
                }}
                icon={<DeleteOutlined/>}
              >
                {translate('remove', 'item')}
              </Button>

              <ShowIf
                condition={!!entity?.id}
                replacement={<></>}>
                <Button
                  disabled={!linesLength}
                  className='ms-1'
                  onClick={() => setReorderModalOpen(true)}
                  icon={<UnorderedListOutlined/>}
                >
                  {translate('appraisal', 'reorder') || 'Reorder'}
                </Button>
                <Button
                  loading={updating}
                  type='primary'
                  className='ms-1'
                  onClick={async () => {
                    const lines = form?.getFieldValue('lines') as AppraisalLineEntry[]
                    const input: AppraisalLineDto[] = lines.map(e => ({
                      itemId: e.item?.id,
                      key: Number(e.key),
                      amount: Number(e.amount),
                      description: e.description,
                      image: e.image?.item?.filename,
                      displayOrder: e.displayOrder
                    }))

                    await update({
                      variables: {
                        id: entity?.id ?? 0,
                        input,
                      },
                    })
                  }}
                  icon={<SaveOutlined/>}
                >
                  {translate('save')}
                </Button>
              </ShowIf>
            </div>

            <div className='w-25 d-flex flex-row justify-content-end'>
              <SinglePageable
                disabled={linesLength === 0}
                onChange={onChangeSlideChange}
                defaultPage={page}
                size='small'
                rowsCaption={translate('items')}
                hideLastButton
                hideFirstButton
              />
            </div>
          </div>

          <AppraisalLinesSlider
            items={fields}
            renderItem={({name: row}) => <Row gutter={[10, 10]}>
              <Col span={18}>
                <Row gutter={[10, 10]}>
                  <Col span={6}>
                    <Form.Item
                      label={translate('item', 'number_abbr')}
                      name={[row, 'item', 'id']}
                    >
                      <AppraisalFormLinesItem
                        row={row}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Form.Item label={translate('number_abbr')}>
                      <Input disabled={true} value={row + 1}/>
                    </Form.Item>
                  </Col>

                  <Col span={6}>
                    <Space>
                      <Form.Item label={translate('of')}>
                        <Input disabled={true} value={fields.length}/>
                      </Form.Item>
                    </Space>
                  </Col>

                  <Col span={6}>
                    <Form.Item
                      label={translate('amount')}
                      name={[row, 'amount']}
                    >
                      <NumericInput prefix='$' style={{padding: "2px 5px"}}/>
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={[10, 10]} className='mt-3'>
                  <Col span={24}>
                    <Form.Item
                      label={translate('description')}
                      name={[row, 'description']}
                    >
                      <Input.TextArea rows={5}/>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col span={6}>
                <Form.Item noStyle name={[row, 'image']}>
                  <Uploader/>
                </Form.Item>
              </Col>
            </Row>
            }
            currentIndex={currentRowIndex}
          />
          {contextHolder}
          {entity?.id && (
            <AppraisalReorderModal
              open={reorderModalOpen}
              onClose={() => setReorderModalOpen(false)}
              onComplete={(lines) => {
                form?.setFieldsValue({ lines })
                setReorderModalOpen(false)
              }}
              lines={form?.getFieldValue('lines') as AppraisalLineEntry[] || []}
            />
          )}
        </>
      )}
    </Form.List>
  )
}


